import {FC, useEffect, useMemo, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Paper,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {AutoAwesome, Edit, Pets, Refresh} from '@mui/icons-material';
import {
    useEppiCostumes,
    usePatchEppiCostume,
} from '../../hooks/useEppiCostumes';
import {useCostumeCategories} from '../../hooks/useCostumeCategory';
import {
    EppiCostume,
    PLACEMENT_TYPE_LABEL_KO,
    SIZE_PRESET_LABEL_KO,
} from '../../types/eppiCostume';
import {
    CostumeCategory,
    RENDER_KIND_CHIP_COLOR,
    RENDER_KIND_LABEL_KO,
} from '../../types/costumeCategory';

const formatDateTime = (s?: string) => {
    if (!s) return '';
    return s.replace('T', ' ').slice(0, 16);
};

interface SortOrderCellProps {
    costume: EppiCostume;
}

const SortOrderCell: FC<SortOrderCellProps> = ({costume}) => {
    const [value, setValue] = useState<number | ''>(costume.sortOrder);
    const patchMut = usePatchEppiCostume();

    const commit = async () => {
        if (value === '' || Number(value) === costume.sortOrder) {
            setValue(costume.sortOrder);
            return;
        }
        try {
            await patchMut.mutateAsync({
                code: costume.code,
                body: {sortOrder: Number(value)},
            });
        } catch {
            setValue(costume.sortOrder);
        }
    };

    return (
        <TextField
            type="number"
            size="small"
            value={value}
            onChange={(e) =>
                setValue(e.target.value === '' ? '' : Number(e.target.value))
            }
            onBlur={commit}
            onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
            disabled={patchMut.isPending}
            sx={{width: 80}}
            inputProps={{style: {textAlign: 'right'}}}
        />
    );
};

interface ActiveSwitchCellProps {
    costume: EppiCostume;
}

const ActiveSwitchCell: FC<ActiveSwitchCellProps> = ({costume}) => {
    const patchMut = usePatchEppiCostume();
    return (
        <Switch
            checked={costume.isActive}
            disabled={patchMut.isPending}
            onChange={(_, checked) => {
                patchMut.mutate({code: costume.code, body: {isActive: checked}});
            }}
            size="small"
        />
    );
};

const PLACEMENT_ICON: Record<'BACK_WALL' | 'FLOOR', string> = {
    BACK_WALL: '🖼️',
    FLOOR: '🪴',
};

interface RoomVisibleSwitchCellProps {
    costume: EppiCostume;
}

const RoomVisibleSwitchCell: FC<RoomVisibleSwitchCellProps> = ({costume}) => {
    const patchMut = usePatchEppiCostume();
    const isArtifact = costume.renderKind === 'ARTIFACT';

    // 공개 가드 — 이미지 + 메타 4종 모두 채워졌는지 클라이언트 사전 점검 (백엔드 5013 과 동일).
    const ready =
        isArtifact &&
        !!costume.imageUrl &&
        costume.tileW != null &&
        costume.tileH != null &&
        !!costume.placementType &&
        !!costume.sizePreset;

    if (!isArtifact) {
        return (
            <Tooltip title="스티커북 노출 토글은 ARTIFACT 슬롯 전용">
                <span>
                    <Switch
                        checked={costume.isRoomEditorVisible}
                        disabled
                        size="small"
                    />
                </span>
            </Tooltip>
        );
    }

    const tooltipForArtifact = !ready && !costume.isRoomEditorVisible
        ? '이미지 finalize + 메타 필수 항목(크기 프리셋·배치 타입·tileW·tileH) 완료 후 공개 가능'
        : '';

    return (
        <Tooltip title={tooltipForArtifact}>
            <span>
                <Switch
                    checked={costume.isRoomEditorVisible}
                    disabled={patchMut.isPending || (!ready && !costume.isRoomEditorVisible)}
                    onChange={(_, checked) => {
                        patchMut.mutate({
                            code: costume.code,
                            body: {isRoomEditorVisible: checked},
                        });
                    }}
                    size="small"
                />
            </span>
        </Tooltip>
    );
};

type CategoryFilter = 'ALL' | number;

export const EppiCostumeList: FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const {data, isLoading, error, refetch} = useEppiCostumes();
    const {data: categoriesData} = useCostumeCategories();
    const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('ALL');

    // deep-link: 카테고리 삭제 차단 다이얼로그에서 넘어왔을 때 자동 필터링
    useEffect(() => {
        const param = searchParams.get('categoryId');
        if (param) {
            const id = Number(param);
            if (!Number.isNaN(id)) setCategoryFilter(id);
        }
    }, [searchParams]);

    const costumes = useMemo(() => data?.items ?? [], [data]);
    const categories: CostumeCategory[] = useMemo(
        () =>
            [...(categoriesData ?? [])].sort(
                (a, b) => a.sortOrder - b.sortOrder || a.id - b.id
            ),
        [categoriesData]
    );

    const categoryById = useMemo(() => {
        const map = new Map<number, CostumeCategory>();
        for (const c of categories) map.set(c.id, c);
        return map;
    }, [categories]);

    const filteredCostumes = useMemo(() => {
        if (categoryFilter === 'ALL') return costumes;
        return costumes.filter((c) => c.categoryId === categoryFilter);
    }, [costumes, categoryFilter]);

    const categoryCounts = useMemo(() => {
        const counts = new Map<number, number>();
        for (const c of costumes) {
            if (c.categoryId == null) continue;
            counts.set(c.categoryId, (counts.get(c.categoryId) ?? 0) + 1);
        }
        return counts;
    }, [costumes]);

    return (
        <Box sx={{p: 3}}>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{mb: 2}}
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Pets color="primary" />
                    <Typography variant="h5" fontWeight={700}>
                        에삐 스티커북
                    </Typography>
                    <Chip
                        size="small"
                        label={`${costumes.length}종`}
                        variant="outlined"
                    />
                </Stack>
                <Stack direction="row" spacing={1}>
                    <Button
                        variant="outlined"
                        onClick={() => navigate('/eppi-categories')}
                    >
                        카테고리 관리
                    </Button>
                    <IconButton onClick={() => refetch()} size="small">
                        <Refresh fontSize="small" />
                    </IconButton>
                    <Button
                        variant="contained"
                        startIcon={<AutoAwesome />}
                        onClick={() => navigate('/eppi-costumes/new')}
                    >
                        새 코스튬
                    </Button>
                </Stack>
            </Stack>

            {/* 카테고리 필터 chips (동적) */}
            <Stack direction="row" spacing={1} sx={{mb: 2}} flexWrap="wrap" useFlexGap>
                <Chip
                    label={`전체 (${costumes.length})`}
                    color={categoryFilter === 'ALL' ? 'primary' : 'default'}
                    variant={categoryFilter === 'ALL' ? 'filled' : 'outlined'}
                    onClick={() => setCategoryFilter('ALL')}
                    size="small"
                />
                {categories.map((cat) => {
                    const count = categoryCounts.get(cat.id) ?? 0;
                    const selected = categoryFilter === cat.id;
                    return (
                        <Chip
                            key={cat.id}
                            label={`${cat.displayNameKo} (${count})`}
                            color={
                                selected
                                    ? RENDER_KIND_CHIP_COLOR[cat.renderKind]
                                    : 'default'
                            }
                            variant={selected ? 'filled' : 'outlined'}
                            onClick={() => setCategoryFilter(cat.id)}
                            size="small"
                        />
                    );
                })}
            </Stack>

            {error && (
                <Alert severity="error" sx={{mb: 2}}>
                    {error.message}
                </Alert>
            )}

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>code</TableCell>
                            <TableCell>카테고리</TableCell>
                            <TableCell>스티커북 메타</TableCell>
                            <TableCell>한국어명</TableCell>
                            <TableCell align="right">가격 (XP)</TableCell>
                            <TableCell>이미지</TableCell>
                            <TableCell align="right">보유자</TableCell>
                            <TableCell align="center">활성</TableCell>
                            <TableCell align="center">방 공개</TableCell>
                            <TableCell align="right">정렬</TableCell>
                            <TableCell>업데이트</TableCell>
                            <TableCell align="right">액션</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading && (
                            <TableRow>
                                <TableCell colSpan={12} align="center" sx={{py: 4}}>
                                    <CircularProgress size={24} />
                                </TableCell>
                            </TableRow>
                        )}
                        {!isLoading && filteredCostumes.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={12} align="center" sx={{py: 4}}>
                                    <Typography variant="body2" color="text.secondary">
                                        {categoryFilter === 'ALL'
                                            ? '등록된 코스튬이 없습니다. "새 코스튬" 버튼으로 시작하세요.'
                                            : `이 카테고리에 등록된 코스튬이 없습니다.`}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {filteredCostumes.map((c) => {
                            const cat = c.categoryId
                                ? categoryById.get(c.categoryId)
                                : undefined;
                            return (
                                <TableRow key={c.id} hover>
                                    <TableCell>
                                        <Typography
                                            variant="body2"
                                            sx={{fontFamily: 'ui-monospace, monospace'}}
                                        >
                                            {c.code}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="column" spacing={0.5}>
                                            <Stack
                                                direction="row"
                                                spacing={0.5}
                                                alignItems="center"
                                                flexWrap="wrap"
                                                useFlexGap
                                            >
                                                {cat ? (
                                                    <Chip
                                                        size="small"
                                                        label={cat.displayNameKo}
                                                        color={
                                                            RENDER_KIND_CHIP_COLOR[
                                                                cat.renderKind
                                                            ]
                                                        }
                                                        variant="outlined"
                                                    />
                                                ) : (
                                                    <Chip
                                                        size="small"
                                                        label="(카테고리 없음)"
                                                        variant="outlined"
                                                        color="warning"
                                                    />
                                                )}
                                                <Chip
                                                    size="small"
                                                    label={
                                                        RENDER_KIND_LABEL_KO[c.renderKind]
                                                    }
                                                    sx={{
                                                        height: 18,
                                                        fontSize: '0.65rem',
                                                    }}
                                                />
                                                {c.isDefault && (
                                                    <Tooltip title="시스템 기본 코스튬 — 미보유 사용자에게 자동 노출">
                                                        <Chip
                                                            size="small"
                                                            label="기본"
                                                            color="success"
                                                        />
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                            {c.renderKind === 'BACKGROUND' && c.bgKind && (
                                                <Chip
                                                    size="small"
                                                    label={c.bgKind}
                                                    sx={{
                                                        height: 18,
                                                        fontSize: '0.65rem',
                                                        bgcolor:
                                                            c.bgKind === 'SOLID'
                                                                ? 'success.50'
                                                                : c.bgKind === 'TILE'
                                                                ? 'info.50'
                                                                : 'grey.100',
                                                        color: 'text.secondary',
                                                    }}
                                                />
                                            )}
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        {c.renderKind === 'ARTIFACT' ? (
                                            <Stack spacing={0.5}>
                                                {c.placementType ? (
                                                    <Chip
                                                        size="small"
                                                        label={`${PLACEMENT_ICON[c.placementType]} ${PLACEMENT_TYPE_LABEL_KO[c.placementType]}`}
                                                        variant="outlined"
                                                        sx={{height: 20, fontSize: '0.7rem'}}
                                                    />
                                                ) : (
                                                    <Typography
                                                        variant="caption"
                                                        color="warning.main"
                                                    >
                                                        placementType 미설정
                                                    </Typography>
                                                )}
                                                {c.sizePreset ? (
                                                    <Chip
                                                        size="small"
                                                        label={SIZE_PRESET_LABEL_KO[c.sizePreset]}
                                                        variant="outlined"
                                                        sx={{height: 20, fontSize: '0.7rem'}}
                                                    />
                                                ) : (
                                                    <Typography
                                                        variant="caption"
                                                        color="warning.main"
                                                    >
                                                        sizePreset 미설정
                                                    </Typography>
                                                )}
                                                {c.tileW != null && c.tileH != null && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {c.tileW}×{c.tileH}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        ) : (
                                            <Typography
                                                variant="caption"
                                                color="text.disabled"
                                            >
                                                N/A
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Stack>
                                            <Typography variant="body2" fontWeight={600}>
                                                {c.nameKo}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                {c.nameEn} / {c.nameJa} / {c.nameVi}
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2">
                                            {c.priceXp.toLocaleString()}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {c.renderKind === 'BACKGROUND' &&
                                        c.bgKind === 'SOLID' &&
                                        c.bgColor ? (
                                            <Tooltip title={`SOLID ${c.bgColor}`}>
                                                <Box
                                                    sx={{
                                                        width: 60,
                                                        height: 60,
                                                        bgcolor: c.bgColor,
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        borderRadius: 1,
                                                    }}
                                                />
                                            </Tooltip>
                                        ) : c.imageUrl ? (
                                            <Tooltip title="원본 보기">
                                                <Box
                                                    component="a"
                                                    href={c.imageUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    sx={{display: 'inline-block'}}
                                                >
                                                    <Box
                                                        component="img"
                                                        src={c.thumbnailUrl || c.imageUrl}
                                                        alt={c.nameKo}
                                                        sx={{
                                                            width: 60,
                                                            height: 60,
                                                            objectFit:
                                                                c.bgKind === 'TILE'
                                                                    ? 'none'
                                                                    : 'contain',
                                                            bgcolor:
                                                                c.bgKind === 'TILE' && c.bgColor
                                                                    ? c.bgColor
                                                                    : 'grey.50',
                                                            backgroundImage:
                                                                c.bgKind === 'TILE'
                                                                    ? `url("${c.imageUrl}")`
                                                                    : undefined,
                                                            backgroundRepeat:
                                                                c.bgKind === 'TILE'
                                                                    ? 'repeat'
                                                                    : undefined,
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                            borderRadius: 1,
                                                        }}
                                                    />
                                                </Box>
                                            </Tooltip>
                                        ) : (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                (없음)
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Chip
                                            size="small"
                                            label={c.ownerCount.toLocaleString()}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <ActiveSwitchCell costume={c} />
                                    </TableCell>
                                    <TableCell align="center">
                                        <RoomVisibleSwitchCell costume={c} />
                                    </TableCell>
                                    <TableCell align="right">
                                        <SortOrderCell costume={c} />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatDateTime(c.updatedAt)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Stack
                                            direction="row"
                                            spacing={0.5}
                                            justifyContent="flex-end"
                                        >
                                            <Tooltip title="편집 (메타 + AI 재생성)">
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        navigate(
                                                            `/eppi-costumes/${c.code}/edit`
                                                        )
                                                    }
                                                >
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};
